import type { AlpineComponent } from 'alpinejs'

type DrivingSchoolSearchArgs = {
  schoolName: null | string
  locationIds: string[]
  licenseIds: number[]
  languageIds: number[]
}

type DrivingSchoolSearchComponent = DrivingSchoolSearchArgs & {
  allowLocationChangeEvent: boolean
  form: HTMLFormElement
  resultsContainer: HTMLElement
  fetchDataAndRender: ({
    url,
    updateHistory,
    shouldScroll,
  }: {
    url: string
    updateHistory?: boolean | undefined
    shouldScroll?: boolean | undefined
  }) => Promise<void>
  loadLink: (event: Event) => void
  locationChanged: (event: Event) => void
  submit: () => void
  updateModelsFromUrl: () => void
  removeFormDataEntryValue: (model: 'schoolName' | 'locationIds' | 'licenseIds' | 'languageIds', value: string) => void
  activeFiltersCount: number
}

export const drivingSchoolSearch = (args: DrivingSchoolSearchArgs): AlpineComponent<DrivingSchoolSearchComponent> => ({
  ...args,
  allowLocationChangeEvent: false,
  form: document.getElementById('school-account-query-form')! as HTMLFormElement,
  resultsContainer: document.getElementById('school-account-query-results')!,

  init() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.submit()
    })

    // When using browser's back/forward
    window.addEventListener('popstate', () => {
      this.fetchDataAndRender({ url: location.toString() })
      this.updateModelsFromUrl()
    })
  },

  async fetchDataAndRender({ url, updateHistory, shouldScroll }) {
    try {
      window.loadingSpinner(true)

      const headers = { Accept: 'text/javascript', 'X-Requested-With': 'XMLHttpRequest' }
      const res = await fetch(url, { headers })
      if (!res.ok) throw new Error()

      this.resultsContainer.innerHTML = await res.text()

      if (updateHistory) {
        history.pushState({ url: res.url }, '', res.url)
      }

      // Scrolls resultsContainer's top into view, useful after pagination requests
      if (shouldScroll) {
        this.$nextTick(() => {
          const headerHeight = document.getElementById('header')!.clientHeight
          if (this.resultsContainer.getBoundingClientRect().top < headerHeight) {
            window.scrollTo({ top: this.resultsContainer.offsetTop - headerHeight })
          }
        })
      }
    } catch {
      alert(window.I18n.unknown_error)
    } finally {
      window.loadingSpinner(false)
    }
  },

  loadLink(event) {
    event.preventDefault()
    if (event.target instanceof HTMLElement) {
      const link = event.target?.closest('a')?.href
      if (link) {
        this.fetchDataAndRender({ url: link, updateHistory: true, shouldScroll: true })
      }
    }
  },

  removeFormDataEntryValue(model, value) {
    if (model === 'schoolName') {
      this.schoolName = value
    } else if (model === 'locationIds') {
      this[model] = this[model].filter((v) => v !== value)
    } else {
      this[model] = this[model].filter((v) => v !== +value)
    }

    // locationIds updates are triggered separately thru CustomSelect @change, so skip
    if (model !== 'locationIds') {
      this.$nextTick(() => this.form.requestSubmit())
    }
  },

  // locationIds CustomSelect emits 'change' events after x-model changes;
  // to prevent double data loading on first page load and on popstate events
  locationChanged() {
    if (this.allowLocationChangeEvent) this.form.requestSubmit()
  },

  submit() {
    const formData = new FormData(this.form)
    const params = new URLSearchParams()

    for (const [key, formDataEntryValue] of formData.entries()) {
      const value = formDataEntryValue.toString()
      if (value == '') continue

      if (params.has(key) && params.get(key) !== value) {
        params.append(key, value)
      } else {
        params.set(key, value)
      }
    }

    this.fetchDataAndRender({ url: `${this.form.action}?${params.toString()}`, updateHistory: true })
  },

  updateModelsFromUrl() {
    this.allowLocationChangeEvent = false

    const urlParams = new URLSearchParams(location.search)
    this.schoolName = urlParams.get('query[school_name]')
    this.locationIds = urlParams
      .getAll('query[location_ids][]')
      .filter((id) => id !== '')
      .map((id) => id)
    this.licenseIds = urlParams
      .getAll('query[license_class_ids][]')
      .filter((id) => id !== '')
      .map((id) => +id)
    this.languageIds = urlParams
      .getAll('query[language_ids][]')
      .filter((id) => id !== '')
      .map((id) => +id)

    this.$nextTick(() => (this.allowLocationChangeEvent = true))
  },

  get activeFiltersCount() {
    return (this.schoolName ? 1 : 0) + this.locationIds.length + this.licenseIds.length + this.languageIds.length
  },
})
